import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { BrandPaletteProvider } from './contexts/BrandPaletteContext';
import AuthLayout from './layouts/auth';
import AdminLayout from './layouts/admin';
import HubLayout from './layouts/hub';
import RootRedirect from './components/RootRedirect';
import SignIn from './views/auth/signIn';
import Dashboard from './views/admin/dashboard';
import ThemePage from './views/admin/theme';
import VillagesPage from './views/admin/master-data/villages';
import BusinessUnitsPage from './views/admin/master-data/business-units';
import EquipmentsPage from './views/admin/master-data/equipments';
import VendorsPage from './views/admin/equipments/vendors';
import VendorDetailPage from './views/admin/equipments/vendors/detail';
import VendorAssetDetailPage from './views/admin/equipments/vendors/assetDetail';
import AppUsersPage from './views/admin/users';
import AppUserDetailPage from './views/admin/users/detail';
import IndentsPage from './views/admin/equipments/indents';
import IndentDetailPage from './views/admin/equipments/indents/detail';
import EquipmentFleetPage from './views/admin/equipments/fleet';
import EquipmentFleetDetailPage from './views/admin/equipments/fleet/detail';
import VillageSummaryPage from './views/admin/equipments/village-summary';
import BuSiteSummaryPage from './views/admin/equipments/bu-site-summary';
import StaffManagementPage from './views/admin/staff';
import StaffDetailPage from './views/admin/staff/detail';
import StaffActivitiesPage from './views/admin/staff/activities';
import SystemConfigPage from './views/admin/system-config';
import AdminHubPage from './views/admin/hub';
import {
  StaffActivitiesGate,
  StaffManagementGate,
  SystemConfigGate,
} from './views/admin/staff/StaffAccessGate';
import PublicLayout from './layouts/public';
import VendorRegisterPage from './views/public/vendor-register';
import VendorPortalPage from './views/public/vendor-portal';
import { AuthLoader } from './components/ui';
import { useAuth } from './contexts/AuthContext';

function ProtectedAdminRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoader label="Checking your session…" />;
  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrandPaletteProvider>
      <Routes>
        <Route path="auth" element={<AuthLayout />}>
          <Route path="sign-in" element={<SignIn />} />
          <Route index element={<Navigate to="sign-in" replace />} />
        </Route>
        <Route path="vendor" element={<PublicLayout />}>
          <Route path="register" element={<VendorRegisterPage />} />
          <Route path="portal/:token" element={<VendorPortalPage />} />
        </Route>
          <Route
            path="admin/hub"
            element={
              <ProtectedAdminRoute>
                <HubLayout />
              </ProtectedAdminRoute>
            }
          >
            <Route index element={<AdminHubPage />} />
          </Route>
          <Route
            path="admin"
            element={
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="theme" element={<ThemePage />} />
            <Route path="equipments/indents" element={<IndentsPage />} />
            <Route path="equipments/indents/:indentId" element={<IndentDetailPage />} />
            <Route path="equipments/fleet" element={<EquipmentFleetPage />} />
            <Route path="equipments/fleet/:assetId" element={<EquipmentFleetDetailPage />} />
            <Route path="equipments/village-summary" element={<VillageSummaryPage />} />
            <Route path="equipments/bu-site-summary" element={<BuSiteSummaryPage />} />
            <Route path="equipments/vendors" element={<VendorsPage />} />
            <Route path="equipments/vendors/:vendorId" element={<VendorDetailPage />} />
            <Route
              path="equipments/vendors/:vendorId/assets/:assetId"
              element={<VendorAssetDetailPage />}
            />
            <Route path="users" element={<AppUsersPage />} />
            <Route path="users/:userId" element={<AppUserDetailPage />} />
            <Route path="master-data/villages" element={<VillagesPage />} />
            <Route path="master-data/business-units" element={<BusinessUnitsPage />} />
            <Route path="master-data/equipments" element={<EquipmentsPage />} />
            <Route
              path="administration/staff"
              element={
                <StaffManagementGate>
                  <StaffManagementPage />
                </StaffManagementGate>
              }
            />
            <Route
              path="administration/staff/:staffId"
              element={
                <StaffManagementGate>
                  <StaffDetailPage />
                </StaffManagementGate>
              }
            />
            <Route
              path="administration/activities"
              element={
                <StaffActivitiesGate>
                  <StaffActivitiesPage />
                </StaffActivitiesGate>
              }
            />
            <Route
              path="administration/system-config"
              element={
                <SystemConfigGate>
                  <SystemConfigPage />
                </SystemConfigGate>
              }
            />
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
    </BrandPaletteProvider>
  );
}
